import { useEffect, useState } from 'react';
import api from '../services/api';
import { emitToast } from '../context/toast';
import ImageSourceField from './admin/ImageSourceField';
import PhoneField from './ui/PhoneField';
import JabelVrPromoCodesPanel from './training/JabelVrPromoCodesPanel';

function loginHeroFromSocial(socialLinks) {
  const links = socialLinks && typeof socialLinks === 'object' ? socialLinks : {};
  const hero = links.login_hero && typeof links.login_hero === 'object' ? links.login_hero : {};
  return {
    login_line_white: String(hero.line_white || '').trim(),
    login_line_yellow_1: String(hero.line_yellow_1 || '').trim(),
    login_line_yellow_2: String(hero.line_yellow_2 || '').trim(),
    login_subtitle: String(hero.subtitle || '').trim(),
  };
}

const DEFAULT_PRIMARY = '#65a30d';
const DEFAULT_SECONDARY = '#1c1917';

export default function CoachBrandingPanel({ trainerId, readOnly = false }) {
  const [form, setForm] = useState({
    brand_name: '',
    logo_url: '',
    primary_color: DEFAULT_PRIMARY,
    secondary_color: DEFAULT_SECONDARY,
    favicon_url: '',
    cover_url: '',
    welcome_message: '',
    support_whatsapp: '',
    white_label_enabled: false,
    invite_code: '',
    whatsapp_waba_id: '',
    whatsapp_phone_number_id: '',
    whatsapp_access_token: '',
    whatsapp_api_enabled: false,
    whatsapp_meta_lang: 'es',
    whatsapp_access_token_set: false,
    social_links: {},
    login_line_white: '',
    login_line_yellow_1: '',
    login_line_yellow_2: '',
    login_subtitle: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');

  useEffect(() => {
    if (!trainerId) return;
    api.get(`/trainers/${trainerId}/branding`)
      .then((r) => setForm((f) => ({
        ...f,
        ...r.data,
        primary_color: r.data?.primary_color || DEFAULT_PRIMARY,
        secondary_color: r.data?.secondary_color || DEFAULT_SECONDARY,
        whatsapp_access_token: '',
        whatsapp_access_token_set: r.data?.whatsapp_access_token_set === true,
        whatsapp_api_enabled: r.data?.whatsapp_api_enabled === true,
        social_links: r.data?.social_links && typeof r.data.social_links === 'object' ? r.data.social_links : {},
        ...loginHeroFromSocial(r.data?.social_links),
        login_url: r.data?.login_url || '',
        register_url: r.data?.register_url || '',
        brand_slug: r.data?.brand_slug || '',
      })))
      .catch(() => {
        emitToast({
          type: 'error',
          title: 'Error',
          message: 'No se pudo cargar la marca del coach',
        });
      });
  }, [trainerId]);

  const save = async () => {
    if (readOnly) return;
    setSaving(true);
    setSaveNotice('');
    try {
      const payload = { ...form };
      if (!String(payload.whatsapp_access_token || '').trim()) {
        delete payload.whatsapp_access_token;
      }
      delete payload.whatsapp_access_token_set;
      const prevLinks = form.social_links && typeof form.social_links === 'object' ? form.social_links : {};
      payload.social_links = {
        ...prevLinks,
        login_hero: {
          line_white: String(form.login_line_white || '').trim(),
          line_yellow_1: String(form.login_line_yellow_1 || '').trim(),
          line_yellow_2: String(form.login_line_yellow_2 || '').trim(),
          subtitle: String(form.login_subtitle || '').trim(),
        },
      };
      delete payload.login_line_white;
      delete payload.login_line_yellow_1;
      delete payload.login_line_yellow_2;
      delete payload.login_subtitle;
      delete payload.login_url;
      delete payload.register_url;
      await api.put(`/trainers/${trainerId}/branding`, payload);
      setSaveNotice('Marca guardada correctamente.');
      try {
        const refreshed = await api.get(`/trainers/${trainerId}/branding`);
        window.dispatchEvent(new CustomEvent('coach-brand-updated', { detail: refreshed.data }));
      } catch {
        window.dispatchEvent(new CustomEvent('coach-brand-updated', { detail: payload }));
      }
      emitToast({
        type: 'success',
        title: 'Guardado',
        message: 'Tu marca de coach fue actualizada.',
      });
    } catch (err) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.error || 'No se pudo guardar la marca',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!trainerId) return null;

  return (
    <div className="training-coach-view training-light-island surface-readable d28d-admin-surface card p-5 space-y-4 mt-4">
      <div>
        <h3 className="text-lg font-bold text-stone-900">Marca del coach</h3>
        <p className="text-sm text-stone-600 mt-1">
          Logo, portada del login, colores, nombre y frases del registro/login Training
          (`training…/login?org=` y el wizard con código de invitación).
          No uses Apariencia de plataforma (eso es GYM VIRTUAL / D28D).
          Si no hay favicon, se usa el logo recortado a cuadrado.
          La portada es la foto de fondo de `training…/login?org=`; si queda vacía, Jabel usa el afiche empaquetado.
        </p>
      </div>

      {readOnly ? <p className="text-sm text-stone-600">Solo lectura</p> : null}

      {saveNotice ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 rounded-lg" role="status">
          {saveNotice}
        </div>
      ) : null}

      <label className="block">
        <span className="text-sm font-semibold text-stone-800">Nombre de marca</span>
        <input
          className="input mt-1 w-full"
          placeholder="Ej. JABEL - NICOLAS"
          value={form.brand_name || ''}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        />
      </label>

      <label className="block">
        <span className="text-sm font-semibold text-stone-800">Código para tus clientes</span>
        <input
          className="input mt-1 w-full font-mono text-sm"
          value={form.invite_code || ''}
          disabled={readOnly}
          readOnly={readOnly}
          onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase().replace(/\s+/g, '') })}
          placeholder="Ej. NICOLAS"
        />
        <p className="text-xs text-stone-500 mt-1">
          Tus clientes lo usan en el registro comercial para vincularse contigo. Debe ser único.
        </p>
      </label>

      {(form.login_url || form.register_url) ? (
        <div className="border border-stone-200 rounded-xl p-4 space-y-2 bg-stone-50/80">
          <h4 className="font-semibold text-stone-900 text-sm">Link único de tu marca</h4>
          <p className="text-xs text-stone-500">
            Compártelo con tus clientes. Cada entrenador tiene el suyo (`?org=` + código de invitación).
          </p>
          {form.login_url ? (
            <label className="block">
              <span className="text-sm font-semibold text-stone-800">Puerta Training (login)</span>
              <input className="input mt-1 w-full font-mono text-xs" value={form.login_url} readOnly />
            </label>
          ) : null}
          {form.register_url ? (
            <label className="block">
              <span className="text-sm font-semibold text-stone-800">Alta de clientes</span>
              <input className="input mt-1 w-full font-mono text-xs" value={form.register_url} readOnly />
            </label>
          ) : null}
          {form.brand_slug ? (
            <p className="text-xs text-stone-500 font-mono">slug: {form.brand_slug}</p>
          ) : null}
        </div>
      ) : null}

      <ImageSourceField
        label="Logo"
        value={form.logo_url || ''}
        onChange={(url) => setForm((prev) => ({ ...prev, logo_url: url }))}
        alt={form.brand_name || 'Logo coach'}
        uploadEndpoint={`/trainers/${trainerId}/logo`}
      />

      <ImageSourceField
        label="Favicon (pestaña)"
        value={form.favicon_url || ''}
        onChange={(url) => setForm((prev) => ({ ...prev, favicon_url: url }))}
        alt="Favicon"
        uploadEndpoint={`/trainers/${trainerId}/logo`}
      />

      <ImageSourceField
        label="Portada del login (foto de fondo)"
        value={form.cover_url || ''}
        onChange={(url) => setForm((prev) => ({ ...prev, cover_url: url }))}
        alt="Portada login"
        uploadEndpoint={`/trainers/${trainerId}/logo?kind=cover`}
      />
      <p className="text-xs text-stone-500 -mt-2">
        Afiche vertical (9:16). Distinto del logo. Tras subir, pulsa <strong>Guardar marca coach</strong>.
        Si dejas la portada vacía, Jabel usa el afiche por defecto.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Color principal</span>
          <div className="flex items-center gap-3 mt-1">
            <input
              type="color"
              className="h-10 w-14 rounded border border-stone-300 cursor-pointer bg-white"
              value={form.primary_color || DEFAULT_PRIMARY}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            />
            <span className="text-xs text-stone-500 font-mono">{form.primary_color || DEFAULT_PRIMARY}</span>
          </div>
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Color secundario</span>
          <div className="flex items-center gap-3 mt-1">
            <input
              type="color"
              className="h-10 w-14 rounded border border-stone-300 cursor-pointer bg-white"
              value={form.secondary_color || DEFAULT_SECONDARY}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
            />
            <span className="text-xs text-stone-500 font-mono">{form.secondary_color || DEFAULT_SECONDARY}</span>
          </div>
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-semibold text-stone-800">Mensaje de bienvenida (opcional)</span>
        <textarea
          className="input mt-1 w-full min-h-[72px]"
          value={form.welcome_message || ''}
          disabled={readOnly}
          onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
          placeholder="Texto breve para tus clientes"
        />
      </label>

      <div className="border border-stone-200 rounded-xl p-4 space-y-3 bg-stone-50/80">
        <h4 className="font-semibold text-stone-900 text-sm">Textos del registro / login</h4>
        <p className="text-xs text-stone-500">
          Línea 1 en blanco; líneas 2 y 3 con el color principal. Vacío = se usan las frases por defecto de la marca.
        </p>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Línea 1 (blanca)</span>
          <input
            className="input mt-1 w-full"
            value={form.login_line_white || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, login_line_white: e.target.value })}
            placeholder="Ej. ENTRENAMIENTO"
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Línea 2 (color principal)</span>
          <input
            className="input mt-1 w-full"
            value={form.login_line_yellow_1 || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, login_line_yellow_1: e.target.value })}
            placeholder="Ej. nombre de tu marca"
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Línea 3 (color principal, opcional)</span>
          <input
            className="input mt-1 w-full"
            value={form.login_line_yellow_2 || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, login_line_yellow_2: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Subtítulo bajo el titular</span>
          <input
            className="input mt-1 w-full"
            value={form.login_subtitle || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, login_subtitle: e.target.value })}
            placeholder="Ej. Texto breve para tus clientes"
          />
        </label>
      </div>

      <PhoneField
        label="WhatsApp de soporte (wa.me)"
        value={form.support_whatsapp || ''}
        onChange={(v) => setForm({ ...form, support_whatsapp: v })}
        disabled={readOnly}
        inputClassName="input mt-1 w-full"
        className="block"
        labelClassName="text-sm font-semibold text-stone-800"
        placeholder="300 123 4567"
      />

      <div className="border border-stone-200 rounded-xl p-4 space-y-3 bg-stone-50/80">
        <h4 className="font-semibold text-stone-900 text-sm">WhatsApp Business API (Meta)</h4>
        <p className="text-xs text-stone-500">
          Para envíos automáticos del Communication Center (Training/Food). Si no configuras WABA, se usa wa.me.
        </p>
        <label className="flex items-center gap-2 text-sm text-stone-800">
          <input
            type="checkbox"
            checked={form.whatsapp_api_enabled === true}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, whatsapp_api_enabled: e.target.checked })}
          />
          Activar WABA
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">WABA ID</span>
          <input
            className="input mt-1 w-full font-mono text-sm"
            value={form.whatsapp_waba_id || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, whatsapp_waba_id: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">Phone number ID</span>
          <input
            className="input mt-1 w-full font-mono text-sm"
            value={form.whatsapp_phone_number_id || ''}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, whatsapp_phone_number_id: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-stone-800">
            Access token{form.whatsapp_access_token_set ? ' (guardado; vacío = no cambiar)' : ''}
          </span>
          <input
            type="password"
            className="input mt-1 w-full font-mono text-sm"
            value={form.whatsapp_access_token || ''}
            disabled={readOnly}
            autoComplete="off"
            onChange={(e) => setForm({ ...form, whatsapp_access_token: e.target.value })}
          />
        </label>
      </div>

      <JabelVrPromoCodesPanel brandSlug={form.brand_slug} readOnly={readOnly} />

      {!readOnly && (
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Guardando…' : 'Guardar marca coach'}
        </button>
      )}
    </div>
  );
}
